import { createSelector } from 'reselect';
import pathOr from 'ramda/src/pathOr';
import { CurrentBusinessUnitSelector } from 'invision-core/src/components/session/businessunit.selectors';
import { NON_BULK_SERVICE_STATUSES } from 'invision-core/src/constants/status.constants';
import i18n from 'invision-core/src/components/i18n/i18n';
import { UserSecurityAttributesSelector } from 'invision-core/src/components/session/session.selectors';
import { hasAccess } from 'invision-core/src/components/security/permission.service';
import { MetadataCodeSelector, MetadataCodeTypeDictionarySelector } from 'invision-core/src/components/metadata/codes/codes.selectors';
import { CODES } from 'invision-core/src/components/metadata/codes/codes.constants';
import { TRANSFER_SERVICES } from '../../security.attributes';
import { SelectedCustomerActiveProductsSelector, IsPIIDataAccessEnabledSelector } from '../../reducers/selectors/customer.selectors';
import { createTableViewModel, getFormattedServiceAttributeValue } from './services.list.selectors.helper';
import CustomerCareLocaleKeys from '../../locales/keys';
import { TYPE_OF_SERVICE } from '../../components/customer/servicesAndUsage/servicesAndSharedEntitlements/services/services.list.constants';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { ConvergentBillerOrderDetailsSelector, CurrentAccountSummarySelector } from './customer.convergent.biller.selectors';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../constants/common.constants';
import { ORDER_CHANGE_TYPE_CODE, ORDER_TYPE_CODE, REASON_ADDITIONAL_PROPERTY_KEYS } from '../../customercare.constants';
import { OrdersSelector } from './order.history.selectors';
import { HasSubscriberBillingAndNetworkSuspensionAccessSelector, HasSubscriberNetworkSuspensionAccessSelector, HasUpdateServiceLifecycleStatusAccessSelector } from './permissions.selectors';
import { SubscriberDeviceFinancingDetailsSelector } from './customer.devices.selectors';
import { addDeviceFinancingToServiceThumbnail } from '../helpers/customer.convergent.biller.selectors.helper';
import { IsFullDisconnect } from './offering.disconnect.selectors';
import { NonTelcoUsageServiceFeatureToggleSelector } from './customer.actions.template.selectors';
var BULK_SERVICE_STATUSES = {
  ACTIVE: 1,
  PENDING_ACTIVE: 3,
  PENDING_REMOVED: 4,
  REMOVED: 2
};
export var SERVICE_ACTION_OPTIONS = {
  SUSPEND: 1,
  RESUME: 2,
  USAGE_CAPS: 3,
  TRANSFER: 4
};
var recoverableUIStateSelector = function recoverableUIStateSelector(state) {
  if (state.customercare.recoverableUiState) {
    return state.customercare.recoverableUiState.bulkServices;
  }
};
export var selectedCustomerBulkItemsSelector = function selectedCustomerBulkItemsSelector(state) {
  return pathOr(null, ['customercare', 'customer', 'selectedCustomer', 'bulkItems'], state);
};
export var BulkServicesPageSizeSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.pageSize;
});
export var BulkServicesSearchStringSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return pathOr(null, ['filterData', 'searchString'], uiState);
});
export var BulkServicesPageNumberSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  return uiState.pageNumber;
});
export var IncludeStatusFilterSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  if (uiState) {
    return pathOr(null, ['filterData', 'includeStatusFilter'], uiState);
  }
});
export var IsFetchingBulkServices = createSelector([selectedCustomerBulkItemsSelector], function (bulkItems) {
  return bulkItems.isFetchingBulkItems;
});
export var IsSuspendingOrResumingServicesSelector = createImmutableSelector([selectedCustomerBulkItemsSelector], function (bulkItems) {
  return bulkItems.isSuspendingOrResumingServices;
});
export var SuspendResumeErrorSelector = createImmutableSelector([selectedCustomerBulkItemsSelector], function (bulkItems) {
  return bulkItems.suspendResumeError;
});
export var BulkServicesStatusesSelector = createSelector([IncludeStatusFilterSelector], function (selectedStatus) {
  var codes = [{
    selected: !selectedStatus || !selectedStatus.value,
    text: i18n.translate(CustomerCareLocaleKeys.ALL),
    value: null
  }, {
    selected: selectedStatus && selectedStatus.value === BULK_SERVICE_STATUSES.ACTIVE,
    text: i18n.translate(CustomerCareLocaleKeys.ACTIVE),
    value: BULK_SERVICE_STATUSES.ACTIVE
  }, {
    selected: selectedStatus && selectedStatus.value === BULK_SERVICE_STATUSES.PENDING_ACTIVE,
    text: i18n.translate(CustomerCareLocaleKeys.PENDING_ACTIVE),
    value: BULK_SERVICE_STATUSES.PENDING_ACTIVE
  }, {
    selected: selectedStatus && selectedStatus.value === BULK_SERVICE_STATUSES.PENDING_REMOVED,
    text: i18n.translate(CustomerCareLocaleKeys.PENDING_REMOVED),
    value: BULK_SERVICE_STATUSES.PENDING_REMOVED
  }, {
    selected: selectedStatus && selectedStatus.value === BULK_SERVICE_STATUSES.REMOVED,
    text: i18n.translate(CustomerCareLocaleKeys.REMOVED),
    value: BULK_SERVICE_STATUSES.REMOVED
  }];
  return codes;
});
export var BulkServicesSelector = createSelector([selectedCustomerBulkItemsSelector], function (currentCustomerBulkItems) {
  return currentCustomerBulkItems ? currentCustomerBulkItems.data : null;
});
export var BulkServicesCurrentPageNumberSelector = createSelector([BulkServicesSelector], function (bulkData) {
  return bulkData ? bulkData.pageNumber : 0;
});
export var BulkServicesCurrentPageCountSelector = createSelector([BulkServicesSelector], function (bulkData) {
  return bulkData ? bulkData.pageCount : 0;
});
export var BulkServicesRecordCountSelector = createSelector([BulkServicesSelector], function (bulkData) {
  return bulkData ? bulkData.recordCount : 0;
});
export var ServicesWithFormattedServiceAttributeSelector = createImmutableSelector([BulkServicesSelector, MetadataCodeTypeDictionarySelector(CODES.ServiceAttribute), MetadataCodeTypeDictionarySelector(CODES.RegularExpression), MetadataCodeSelector(CODES.ServiceLifeCycleStatus)], function (services, serviceAttributes, regularExpressions, serviceLifeCycleStatusDetails) {
  if (!services) {
    return EMPTY_OBJECT;
  }
  return Object.assign({}, services, {
    serviceThumbnails: (services.serviceThumbnails || EMPTY_ARRAY).map(function (serviceThumbnail) {
      return Object.assign({}, serviceThumbnail, {
        ServiceAttributeValues: serviceThumbnail.ServiceAttributeValues && serviceThumbnail.ServiceAttributeValues.map(function (serviceAttribute) {
          return Object.assign({}, serviceAttribute, {
            FormattedValue: getFormattedServiceAttributeValue(serviceAttribute.ServiceAttributeId, serviceAttribute.Value, serviceAttributes, regularExpressions)
          });
        }),
        ServiceLifeCycleStatusDetails: serviceThumbnail.ServiceLifeCycleStatus ? pathOr(EMPTY_ARRAY, ['items'], serviceLifeCycleStatusDetails).find(function (lifeCycleStatus) {
          return lifeCycleStatus.Value === serviceThumbnail.ServiceLifeCycleStatus;
        }) : undefined,
        ServiceIdentifierFormatted: getFormattedServiceAttributeValue(serviceThumbnail.ServiceIdentifierAttributeId, serviceThumbnail.ServiceIdentifier, serviceAttributes, regularExpressions)
      });
    })
  });
});
export var ServicesWithDeviceFinancingInfoSelector = createImmutableSelector([ServicesWithFormattedServiceAttributeSelector, SubscriberDeviceFinancingDetailsSelector], function (servicesData, deviceFinancingInfo) {
  if (deviceFinancingInfo && deviceFinancingInfo.length) {
    return Object.assign({}, servicesData, {
      serviceThumbnails: addDeviceFinancingToServiceThumbnail(servicesData.serviceThumbnails, deviceFinancingInfo)
    });
  }
  return servicesData;
});
export var BulkServicesTableDataSelector = createSelector([IsFetchingBulkServices, ServicesWithDeviceFinancingInfoSelector, CurrentBusinessUnitSelector, SelectedCustomerActiveProductsSelector, CurrentAccountSummarySelector, OrdersSelector, MetadataCodeSelector(CODES.UnitsOfMeasure), NonTelcoUsageServiceFeatureToggleSelector], function (isFetchingBulkServices, services, currentBusinessUnit, selectedCustomerActiveProducts, currentAccountSummary, ordersSelector, units, nonTelcoUsageService) {
  return isFetchingBulkServices === false && units ? createTableViewModel(services, currentBusinessUnit.currencyCode, selectedCustomerActiveProducts, (currentAccountSummary || {}).Services, ordersSelector || [], units, nonTelcoUsageService) : EMPTY_ARRAY;
});
export var ReasonCodesSelector = createImmutableSelector([MetadataCodeSelector(CODES.ReasonCodes)], function (serviceReasonCodes) {
  return serviceReasonCodes && serviceReasonCodes.items ? serviceReasonCodes.items : EMPTY_ARRAY;
});
export var SuspendServiceReasonCodesSelector = createImmutableSelector([ReasonCodesSelector], function (reasonCodes) {
  if (reasonCodes) {
    return reasonCodes.filter(function (reason) {
      if (reason.AdditionalProperties.find(function (item) {
        return item.Key === 'order_type_code';
      }).Value === ORDER_TYPE_CODE.SUSPEND && (reason.AdditionalProperties.find(function (item) {
        return item.Key === 'order_change_type_code';
      }).Value === ORDER_CHANGE_TYPE_CODE.VOLUNTARY_SUSPENSION || reason.AdditionalProperties.find(function (item) {
        return item.Key === 'order_change_type_code';
      }).Value === ORDER_CHANGE_TYPE_CODE.NON_PAY_SUSPENSION)) {
        return reason;
      }
    });
  }
});
export var ResumeServiceReasonCodesSelector = createImmutableSelector([ReasonCodesSelector], function (reasonCodes) {
  if (reasonCodes) {
    return reasonCodes.filter(function (reason) {
      if (reason.AdditionalProperties.find(function (item) {
        return item.Key === 'order_type_code';
      }).Value === ORDER_TYPE_CODE.RESTORE && (reason.AdditionalProperties.find(function (item) {
        return item.Key === 'order_change_type_code';
      }).Value === ORDER_CHANGE_TYPE_CODE.VOLUNTARY_SUSPENSION || reason.AdditionalProperties.find(function (item) {
        return item.Key === 'order_change_type_code';
      }).Value === ORDER_CHANGE_TYPE_CODE.NON_PAY_SUSPENSION)) {
        return reason;
      }
    });
  }
});
export var DisconnectAndRemoveOfferCustomerReasonCodesSelector = createImmutableSelector([ReasonCodesSelector, IsFullDisconnect], function (reasonCodes, IsFullDisconnect) {
  var disconnectAndRemoveOfferReasons = (reasonCodes || EMPTY_ARRAY).filter(function (reason) {
    return reason.AdditionalProperties.find(function (reasonAdditionalProperty) {
      if (IsFullDisconnect) {
        if (reasonAdditionalProperty.Key === REASON_ADDITIONAL_PROPERTY_KEYS.ORDER_CHANGE_TYPE_CODE && reasonAdditionalProperty.Value === ORDER_CHANGE_TYPE_CODE.DISCONNECT) {
          return reason;
        }
      } else {
        if (reasonAdditionalProperty.Key === REASON_ADDITIONAL_PROPERTY_KEYS.ORDER_TYPE_CODE && reasonAdditionalProperty.Value === ORDER_TYPE_CODE.CHANGE_OF_SERVICE) {
          return reason;
        }
      }
    });
  });
  return disconnectAndRemoveOfferReasons || EMPTY_ARRAY;
});
export var DisconnectReasonForOrderDetailsSelector = createImmutableSelector([ConvergentBillerOrderDetailsSelector, ReasonCodesSelector], function (convergentBillerOrderDetails, disconnectCustomerReasons) {
  var disconnectReason = (disconnectCustomerReasons || []).find(function (item) {
    return convergentBillerOrderDetails && convergentBillerOrderDetails.ReasonCode && convergentBillerOrderDetails.ReasonCode.toString() === item.Value;
  });
  return disconnectReason && disconnectReason.Name;
});
export var ActiveServicesSelector = createImmutableSelector([BulkServicesTableDataSelector], function (bulkServicesTableData) {
  return bulkServicesTableData.filter(function (service) {
    return service.ServiceStatus === NON_BULK_SERVICE_STATUSES.ACTIVE;
  });
});
export var SuspendedServicesSelector = createImmutableSelector([BulkServicesTableDataSelector], function (bulkServicesTableData) {
  return bulkServicesTableData.filter(function (service) {
    return service.ServiceStatus === NON_BULK_SERVICE_STATUSES.SUSPENDED;
  });
});
export var HasOffCycleChargesSelector = createImmutableSelector([BulkServicesTableDataSelector], function (bulkServicesTableData) {
  return bulkServicesTableData.some(function (service) {
    return service.HasOffCycleCharges;
  });
});
export var HasActiveServicesSelector = createImmutableSelector([ActiveServicesSelector], function (activeServices) {
  return !!activeServices.length;
});
export var HasSuspendedServicesSelector = createImmutableSelector([SuspendedServicesSelector], function (suspendedServices) {
  return !!suspendedServices.length;
});
export var HasSuspendResumePermissionSelector = createImmutableSelector([HasSubscriberBillingAndNetworkSuspensionAccessSelector, HasSubscriberNetworkSuspensionAccessSelector], function (networkAndBillingSuspensionPermission, networkOnlyBillingSuspensionPermission) {
  return networkAndBillingSuspensionPermission || networkOnlyBillingSuspensionPermission;
});
export var ServicesSuspendResumeMenuOptionsSelector = createSelector([HasActiveServicesSelector, HasSuspendedServicesSelector, UserSecurityAttributesSelector, HasSuspendResumePermissionSelector, IsPIIDataAccessEnabledSelector], function (hasActiveServices, hasSuspendedServices, userSecurityAttributes, hasSuspendResumeAccess, isPIIDataAccessEnabled) {
  var menuOptions = [];
  if (hasSuspendResumeAccess) {
    if (hasActiveServices) {
      menuOptions.push({
        title: i18n.translate(CustomerCareLocaleKeys.SUSPEND_SERVICES),
        id: SERVICE_ACTION_OPTIONS.SUSPEND
      });
    }
    if (hasSuspendedServices) {
      menuOptions.push({
        title: i18n.translate(CustomerCareLocaleKeys.RESUME_SERVICES),
        id: SERVICE_ACTION_OPTIONS.RESUME
      });
    }
  }
  menuOptions.push({
    title: i18n.translate(CustomerCareLocaleKeys.USAGE_CAPS.USAGE_CONTROLS_LABEL),
    id: SERVICE_ACTION_OPTIONS.USAGE_CAPS
  });
  if ((hasActiveServices || hasSuspendedServices) && hasAccess(userSecurityAttributes, TRANSFER_SERVICES) && isPIIDataAccessEnabled) {
    menuOptions.push({
      title: i18n.translate(CustomerCareLocaleKeys.TRANSFER.TRANSFER_SERVICES),
      id: SERVICE_ACTION_OPTIONS.TRANSFER
    });
  }
  return menuOptions;
});
export var BulkServicesSearchErrorSelector = createSelector([selectedCustomerBulkItemsSelector], function (bulkItems) {
  return bulkItems.bulkItemError;
});
export var IncludeTypeOfServiceFilterSelector = createSelector([recoverableUIStateSelector], function (uiState) {
  if (uiState) {
    return pathOr(null, ['filterData', 'includeTypeOfServiceFilter'], uiState);
  }
});
export var ServicesTypeSelector = createSelector([IncludeTypeOfServiceFilterSelector], function (selectedTypeOfService) {
  var codes = [{
    selected: !selectedTypeOfService || !selectedTypeOfService.value,
    text: i18n.translate(CustomerCareLocaleKeys.ALL),
    value: null
  }, {
    selected: selectedTypeOfService && selectedTypeOfService.value === TYPE_OF_SERVICE.BULK,
    text: i18n.translate(CustomerCareLocaleKeys.SERVICES_LIST.TYPES.BULK),
    value: TYPE_OF_SERVICE.BULK
  }, {
    selected: selectedTypeOfService && selectedTypeOfService.value === TYPE_OF_SERVICE.NON_BULK,
    text: i18n.translate(CustomerCareLocaleKeys.SERVICES_LIST.TYPES.NON_BULK),
    value: TYPE_OF_SERVICE.NON_BULK
  }];
  return codes;
});
export var IsServiceLifeCycleStatusUpdatableSelector = createImmutableSelector([HasUpdateServiceLifecycleStatusAccessSelector, MetadataCodeSelector(CODES.ServiceLifeCycleStatus)], function (hasUpdateServiceLifecycleStatusAccess, serviceLifecycleStatusCodes) {
  var _serviceLifecycleStat;
  return hasUpdateServiceLifecycleStatusAccess && !!(serviceLifecycleStatusCodes !== null && serviceLifecycleStatusCodes !== void 0 && (_serviceLifecycleStat = serviceLifecycleStatusCodes.items) !== null && _serviceLifecycleStat !== void 0 && _serviceLifecycleStat.length);
});
export var IsUpdatingServiceTaxSelector = createImmutableSelector([selectedCustomerBulkItemsSelector], function (bulkItems) {
  return bulkItems.isUpdatingServiceTax;
});